
/**
 * Utility functions for consistent date handling across the application
 * Prevents timezone issues by working with local dates only
 */

/**
 * Converts a Date object to YYYY-MM-DD string format
 * This avoids timezone issues when storing/retrieving dates
 */
export const formatDateForStorage = (date: Date): string => {
  return date.getFullYear() + '-' + 
    String(date.getMonth() + 1).padStart(2, '0') + '-' + 
    String(date.getDate()).padStart(2, '0');
};

/**
 * Converts a YYYY-MM-DD string to a Date object
 * Assumes the date is in local timezone
 */
export const parseDateFromStorage = (dateString: string): Date => {
  // Handle empty or invalid strings
  if (!dateString || typeof dateString !== 'string' || dateString.trim() === '') {
    // Return default date (15 days from today)
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 15);
    return defaultDate;
  }
  
  // Split the date string and create date with local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Validate the parsed numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    // Return default date if parsing fails
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 15);
    return defaultDate;
  }
  
  return new Date(year, month - 1, day, 12, 0, 0); // Create at noon to avoid timezone issues
};

/**
 * Formats a date string or Date object for display in Brazilian format
 */
export const formatDateForDisplay = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  // Handle YYYY-MM-DD strings directly to avoid timezone issues
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-');
    const monthNames = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    return `${parseInt(day)} de ${monthNames[parseInt(month) - 1]} de ${year}`;
  }
  
  // Fallback for other date formats
  let dateObj: Date;
  
  if (typeof date === 'string') {
    dateObj = parseDateFromStorage(date);
  } else {
    dateObj = date;
  }
  
  return dateObj.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Safely processes date input to ensure consistent YYYY-MM-DD format
 * Handles various input formats (ISO strings, Date objects, YYYY-MM-DD strings)
 */
export const normalizeDate = (dateInput: string | Date): string => {
  if (typeof dateInput === 'string') {
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      return dateInput;
    }
    
    // If it's an ISO string or other format, convert to Date first
    const dateObj = new Date(dateInput);
    return formatDateForStorage(dateObj);
  } else if (dateInput instanceof Date) {
    return formatDateForStorage(dateInput);
  }
  
  throw new Error('Invalid date input format');
};

/**
 * Checks if a date is a weekend (Saturday or Sunday)
 */
const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday = 0, Saturday = 6
};

/**
 * Calculates delivery date for decoration and disposable suppliers
 * Returns 2 business days before the event date
 */
export const calculateDeliveryDate = (eventDate: string | Date): string => {
  let baseDate: Date;
  
  if (typeof eventDate === 'string') {
    baseDate = parseDateFromStorage(eventDate);
  } else {
    baseDate = new Date(eventDate);
  }
  
  let deliveryDate = new Date(baseDate);
  let businessDaysSubtracted = 0;
  
  // Subtract days until we have 2 business days before
  while (businessDaysSubtracted < 2) {
    deliveryDate.setDate(deliveryDate.getDate() - 1);
    
    // If it's not a weekend, count it as a business day
    if (!isWeekend(deliveryDate)) {
      businessDaysSubtracted++;
    }
  }
  
  return formatDateForStorage(deliveryDate);
};

/**
 * Determines if a supplier category needs early delivery (2 business days before)
 */
export const needsEarlyDelivery = (category: string): boolean => {
  const earlyDeliveryCategories = [
    'decoracao_descartaveis', 
    'decoracao', 
    'descartaveis', 
    'decoration', 
    'disposables'
  ];
  return earlyDeliveryCategories.includes(category.toLowerCase());
};

/**
 * Formats a date string or Date object for display in Brazilian short format (DD/MM/YYYY)
 */
export const formatDateToBrazilianShort = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  // Handle YYYY-MM-DD strings directly to avoid timezone issues
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-');
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  
  // Fallback for other date formats
  let dateObj: Date;
  
  if (typeof date === 'string') {
    dateObj = parseDateFromStorage(date);
  } else {
    dateObj = date;
  }
  
  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Checks if the event date is within 5 days or less from today
 */
export const isWithinFiveDays = (eventDate: string): boolean => {
  const today = new Date();
  const eventDateObj = parseDateFromStorage(eventDate);
  const diffTime = eventDateObj.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 5;
};

/**
 * Calculates the date when supplier payment will be made using backend calculation
 * Returns YYYY-MM-DD string format for database storage
 * @deprecated Use the backend calculate_payment_date function instead
 */
export const calculateDateSplit = async (): Promise<string> => {
  console.warn('⚠️ calculateDateSplit is deprecated. Use backend calculation instead.');
  
  // Fallback calculation (should not be used in production)
  const currentDate = new Date();
  const paymentDate = new Date(currentDate.getTime() + (14 * 24 * 60 * 60 * 1000));
  return paymentDate.toISOString().split('T')[0];
};
